import React, { useCallback, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Form from '@atlaskit/form';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';

import { fg } from '@confluence/feature-gating';

import {
	useAiBulkNavigationCurrentPage,
	useAiBulkNavigationGotoPage,
} from '../../providers/AiBulkNavigationContextProvider';
import {
	useBulkCreateContext,
	useGetIsMissingColumnMappingError,
} from '../../providers/BulkCreateContextProvider';
import { messages } from '../../messages';
import { SidePanelWrapper } from '../work-create-common/SidePanelWrapper';
import {
	useAvailableSitesState,
	useIsConfigureLoading,
	useIssueTypesState,
	useMappingsActions,
	useProjectsActions,
	useProjectsState,
	useResetConfigureState,
	useAvailableSitesActions,
	useIssueTypesActions,
} from '../../providers/BulkConfigureContextProvider';
import { saveJiraConfigDetailsToLocalStorage } from '../../utils/localStorageHelpers';
import { BulkCreateNavigationHeader } from '../bulk-work-create-common/BulkCreateNavigationHeader';
import {
	SiteSelector,
	IssueTypeSelector as DefaultIssueTypeSelector,
	ProjectSelector,
} from '../work-create-form-common';

import { ColumnToIssueFieldMappingView } from './ColumnToIssueFieldMappingView';
import { BulkConfigureFooter } from './BulkConfigureFooter';

export type MappingViewRefType = {
	validateMappings: () => boolean;
};

const bulkConfigureViewStyles = xcss({
	flexGrow: 1,
	overflowY: 'auto',
});

// This is the view for both AI and non-AI bulk create where users can edit and create individual issues
export const BulkConfigureView = ({ closePanel = () => {} }: { closePanel?: () => void }) => {
	const [
		{ isCreatedWithAi, currentJiraSite, currentJiraProject, currentDefaultIssueType },
		{ setCurrentJiraSite, setCurrentJiraProject, setCurrentDefaultIssueType },
	] = useBulkCreateContext();

	const { formatMessage } = useIntl();
	const { optionsAvailableSites, selectedAvailableSite } = useAvailableSitesState();
	const { setSelectedAvailableSite } = useAvailableSitesActions();
	const { selectedProject, optionsRecentProjects, optionsAllProjects } = useProjectsState();
	const { setSelectedProject, setProjectSelectorQuery } = useProjectsActions();
	const { selectedIssueType, optionsIssueTypes } = useIssueTypesState();
	const { setSelectedIssueType } = useIssueTypesActions();
	const { isLoading, isJiraError } = useIsConfigureLoading();
	const resetConfigureState = useResetConfigureState();
	const { setIsJiraError } = useProjectsActions();
	const {
		setUnsavedColumnToFieldMappings,
		initUnsavedColumnToFieldMappings,
		saveUnsavedColumnToFieldMapping,
	} = useMappingsActions();

	const goToPage = useAiBulkNavigationGotoPage();
	const currentPage = useAiBulkNavigationCurrentPage();

	const isOpen = currentPage === 'configure';
	const isMissingColumnMappingError = useGetIsMissingColumnMappingError();

	const mappingViewRef = useRef<MappingViewRefType | null>(null);

	const onSubmit = useCallback(() => {
		if (!isCreatedWithAi) {
			if (mappingViewRef.current?.validateMappings()) {
				saveUnsavedColumnToFieldMapping();
			} else {
				return;
			}
		}
		selectedAvailableSite && void setCurrentJiraSite(selectedAvailableSite);
		selectedProject && void setCurrentJiraProject(selectedProject);
		selectedIssueType && void setCurrentDefaultIssueType(selectedIssueType);
		saveJiraConfigDetailsToLocalStorage({
			selectedSite: selectedAvailableSite,
			selectedProject,
			selectedIssueType,
		});
		void goToPage('default');
	}, [
		selectedAvailableSite,
		setCurrentJiraSite,
		selectedProject,
		setCurrentJiraProject,
		selectedIssueType,
		setCurrentDefaultIssueType,
		isCreatedWithAi,
		saveUnsavedColumnToFieldMapping,
		goToPage,
	]);

	const onClose = useCallback(() => {
		if (currentJiraSite && currentJiraProject && currentDefaultIssueType) {
			void resetConfigureState(currentJiraSite, currentJiraProject, currentDefaultIssueType);
		}
		if (!isCreatedWithAi) {
			setUnsavedColumnToFieldMappings(null);
			initUnsavedColumnToFieldMappings();
		}
		void setIsJiraError(false);
		void goToPage('default');
	}, [
		currentDefaultIssueType,
		currentJiraProject,
		currentJiraSite,
		goToPage,
		resetConfigureState,
		setIsJiraError,
		isCreatedWithAi,
		setUnsavedColumnToFieldMappings,
		initUnsavedColumnToFieldMappings,
	]);

	const isSaveDisabled =
		isLoading || !selectedAvailableSite || !selectedProject || !selectedIssueType;
	const bulkCreateConfigureBodyText = fg('confluence-issue-terminology-refresh')
		? messages.bulkCreateConfigureBodyTextIssueTermRefresh
		: messages.bulkCreateConfigureBodyText;
	return (
		<SidePanelWrapper isOpen={isOpen}>
			<Box padding="space.200" xcss={bulkConfigureViewStyles}>
				<Stack space="space.100">
					<BulkCreateNavigationHeader onClose={onClose}>
						<FormattedMessage {...messages.bulkConfigureHeaderText} />
					</BulkCreateNavigationHeader>
					<Text color="color.text.subtlest">
						<FormattedMessage {...bulkCreateConfigureBodyText} />
					</Text>
					{isJiraError && (
						<SectionMessage appearance="error" testId="bulkCreateErrorSectionMessage">
							<FormattedMessage {...messages.bulkConfigureJiraErrorText} />
						</SectionMessage>
					)}
					<Stack space="space.300">
						<Form onSubmit={onSubmit}>
							{({ formProps }) => (
								<form id="configureForm" {...formProps}>
									{fg('jira_ai_decouple_issue_create_fields') ? (
										<>
											<SiteSelector
												options={optionsAvailableSites ?? []}
												value={selectedAvailableSite}
												onChange={(site) => site && setSelectedAvailableSite(site)}
												isLoading={isLoading}
												isDisabled={isLoading}
												label={formatMessage(messages.bulkConfigureFormSite)}
											/>
											<ProjectSelector
												optionsAllProjects={optionsAllProjects ?? []}
												optionsRecentProjects={optionsRecentProjects ?? []}
												value={selectedProject}
												onChange={(project) => project && setSelectedProject(project)}
												isLoading={isLoading}
												isDisabled={isLoading}
												onInputChange={(query) => setProjectSelectorQuery(query)}
												label={formatMessage(messages.bulkConfigureFormProject)}
												labelProjectSelectorRecentProjects={formatMessage(
													messages.bulkConfigureFormProjectSelectorRecentProjects,
												)}
												labelProjectSelectorProjects={formatMessage(
													messages.bulkConfigureFormProjectSelectorProjects,
												)}
											/>
											<DefaultIssueTypeSelector
												optionsIssueTypes={optionsIssueTypes ?? []}
												value={selectedIssueType}
												onChange={(issueType) => issueType && setSelectedIssueType(issueType)}
												isLoading={isLoading}
												isDisabled={isLoading}
												label={formatMessage(
													fg('confluence-issue-terminology-refresh')
														? messages.bulkConfigureFormDefaultIssueTypeIssueTermRefresh
														: messages.bulkConfigureFormDefaultIssueType,
												)}
											/>
										</>
									) : (
										<>
											<SiteSelector />
											<ProjectSelector />
											<DefaultIssueTypeSelector />
										</>
									)}
								</form>
							)}
						</Form>
						{isMissingColumnMappingError && (
							<SectionMessage
								appearance="information"
								title={
									messages.bulkConfigureMissingTableCoulumnMappingErrorHeaderText.defaultMessage
								}
								testId="bulkCreateWarningSectionMessage"
							>
								<FormattedMessage {...messages.bulkConfigureMissingTableCoulumnMappingErrorText} />
							</SectionMessage>
						)}
						{!isCreatedWithAi && <ColumnToIssueFieldMappingView ref={mappingViewRef} />}
					</Stack>
				</Stack>
			</Box>
			<BulkConfigureFooter
				onClose={onClose}
				closePanel={closePanel}
				isSaveDisabled={isSaveDisabled}
			/>
		</SidePanelWrapper>
	);
};
